import React from 'react';
import { motion } from 'framer-motion';
import Icon from '../../../components/AppIcon';

const RegulationsTimelineSection = () => {
  const timeline = [
    {
      year: '2024',
      status: 'current',
      title: 'Huidige regelgeving',
      items: [
        { text: 'ISDE subsidie beschikbaar voor warmtepompen', icon: 'CheckCircle', color: 'text-success' },
        { text: 'BTW-teruggave zonnepanelen actief', icon: 'CheckCircle', color: 'text-success' },
        { text: 'SEEH subsidie voor isolatie', icon: 'CheckCircle', color: 'text-success' },
        { text: 'Salderingsregeling nog volledig van kracht', icon: 'CheckCircle', color: 'text-success' }
      ]
    },
    {
      year: '2025',
      status: 'upcoming',
      title: 'Aankomende wijzigingen',
      items: [
        { text: 'Salderingsregeling wordt afgebouwd naar 70%', icon: 'AlertCircle', color: 'text-warning' },
        { text: 'Nieuwe subsidie voor thuisbatterijen verwacht', icon: 'TrendingUp', color: 'text-primary' },
        { text: 'Uitbreiding ISDE budget aangekondigd', icon: 'TrendingUp', color: 'text-primary' },
        { text: 'Strengere eisen energielabels huurwoningen', icon: 'AlertCircle', color: 'text-warning' }
      ]
    },
    {
      year: '2026',
      status: 'future',
      title: 'Toekomstige plannen',
      items: [
        { text: 'Salderingsregeling verder afgebouwd naar 40%', icon: 'TrendingDown', color: 'text-warning' },
        { text: 'Mogelijke verhoging ISDE bedragen', icon: 'TrendingUp', color: 'text-primary' },
        { text: 'Nieuwe regelgeving voor hybride systemen', icon: 'Info', color: 'text-blue-600' },
        { text: 'Uitbreiding lokale subsidies verwacht', icon: 'TrendingUp', color: 'text-primary' }
      ]
    },
    {
      year: '2027-2030',
      status: 'longterm',
      title: 'Lange termijn doelen',
      items: [
        { text: 'Salderingsregeling volledig afgeschaft (2031)', icon: 'X', color: 'text-destructive' },
        { text: 'Alle nieuwbouw gasloos vanaf 2026', icon: 'Home', color: 'text-primary' },
        { text: 'Verbod op HR-ketels in nieuwbouw', icon: 'Ban', color: 'text-destructive' },
        { text: 'Energielabel C minimaal voor alle woningen', icon: 'TrendingUp', color: 'text-primary' }
      ]
    }
  ];

  const getStatusColor = (status) => {
    const colors = {
      current: 'from-green-500 to-emerald-500',
      upcoming: 'from-blue-500 to-cyan-500',
      future: 'from-purple-500 to-pink-500',
      longterm: 'from-orange-500 to-red-500'
    };
    return colors?.[status];
  };

  return (
    <section className="py-12 sm:py-16 lg:py-20 bg-gradient-to-br from-gray-50 to-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <h2 className="text-3xl sm:text-4xl font-heading font-bold text-foreground mb-4">
            Tijdlijn regelgeving & subsidies
          </h2>
          <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
            Blijf op de hoogte van huidige en toekomstige wijzigingen
          </p>
        </motion.div>

        {/* Timeline */}
        <div className="relative">
          {/* Vertical Line */}
          <div className="hidden md:block absolute left-8 top-0 bottom-0 w-1 bg-gradient-to-b from-green-200 via-blue-200 via-purple-200 to-orange-200" />

          {/* Timeline Items */}
          <div className="space-y-8">
            {timeline?.map((period, index) => (
              <motion.div
                key={period?.year}
                initial={{ opacity: 0, x: -50 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="relative"
              >
                {/* Year Badge */}
                <div className="flex items-start space-x-6">
                  <motion.div
                    whileHover={{ scale: 1.1, rotate: 360 }}
                    transition={{ duration: 0.6 }}
                    className={`relative z-10 w-16 h-16 bg-gradient-to-br ${getStatusColor(period?.status)} rounded-full flex items-center justify-center shadow-elevation-3 flex-shrink-0`}
                  >
                    <span className="text-lg font-bold text-white">{period?.year}</span>
                  </motion.div>

                  {/* Content Card */}
                  <motion.div
                    whileHover={{ scale: 1.01, y: -2 }}
                    className="flex-1 bg-white rounded-2xl p-6 shadow-elevation-2 border border-gray-200"
                  >
                    <h3 className="text-xl font-bold text-foreground mb-4">{period?.title}</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {period?.items?.map((item, i) => (
                        <motion.div
                          key={i}
                          initial={{ opacity: 0, x: -10 }}
                          whileInView={{ opacity: 1, x: 0 }}
                          viewport={{ once: true }}
                          transition={{ duration: 0.4, delay: index * 0.1 + i * 0.05 }}
                          className="flex items-start space-x-3 p-3 rounded-lg hover:bg-gray-50 transition-colors"
                        >
                          <Icon name={item?.icon} size={18} className={`${item?.color} mt-0.5 flex-shrink-0`} />
                          <span className="text-sm text-muted-foreground">{item?.text}</span>
                        </motion.div>
                      ))}
                    </div>
                  </motion.div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Important Notice */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="mt-12 bg-yellow-50 border border-yellow-200 rounded-xl p-6"
        >
          <div className="flex items-start space-x-4">
            <div className="flex-shrink-0">
              <div className="w-12 h-12 bg-yellow-500/10 rounded-full flex items-center justify-center">
                <Icon name="AlertTriangle" size={24} className="text-yellow-600" />
              </div>
            </div>
            <div>
              <h4 className="text-lg font-bold text-foreground mb-2">
                Let op: regelgeving kan wijzigen
              </h4>
              <p className="text-sm text-muted-foreground leading-relaxed mb-3">
                De informatie op deze pagina is actueel per vandaag, maar subsidies en regelgeving kunnen 
                wijzigen. Check altijd de meest recente informatie op officiële websites voordat u een 
                aanvraag indient.
              </p>
              <div className="flex flex-wrap gap-3">
                <motion.a
                  href="https://www.rvo.nl"
                  target="_blank"
                  rel="noopener noreferrer"
                  whileHover={{ scale: 1.05 }}
                  className="inline-flex items-center space-x-2 px-4 py-2 bg-primary text-white rounded-lg font-semibold text-sm shadow-elevation-1 hover:shadow-elevation-2 transition-shadow"
                >
                  <span>RVO.nl</span>
                  <Icon name="ExternalLink" size={14} />
                </motion.a>
                <motion.a
                  href="https://www.verbeterjehuis.nl"
                  target="_blank"
                  rel="noopener noreferrer"
                  whileHover={{ scale: 1.05 }}
                  className="inline-flex items-center space-x-2 px-4 py-2 bg-white text-primary border border-primary rounded-lg font-semibold text-sm shadow-elevation-1 hover:shadow-elevation-2 transition-shadow"
                >
                  <span>VerbeterJeHuis.nl</span>
                  <Icon name="ExternalLink" size={14} />
                </motion.a>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default RegulationsTimelineSection;